import * as i0 from '@angular/core';
import { ElementRef, Component, Input, ViewChild, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from 'se-fe-inline-list';
import { SeFeInlineListModule } from 'se-fe-inline-list';

class SeFeHeaderComponent {
    constructor(el, cdRef) {
        this.el = el;
        this.cdRef = cdRef;
        this.size = 'default';
        this._level = '2';
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes || mutation.removedNodes) {
                    this.updateContent();
                    this.cdRef.detectChanges();
                }
            });
        });
    }
    set seFeDataTitle(titleText) {
        this.titleText = titleText;
    }
    set seFeDataSubtitle(subtitleText) {
        this.subtitleText = subtitleText;
    }
    set seFeDataSize(size) {
        this.size = size;
    }
    set seFeDataLevel(level) {
        this._level = level;
    }
    get level() {
        return Number(this._level) > 0 ? this._level : null;
    }
    ngOnInit() {
        this.observer.observe(this.element, {
            childList: true,
            subtree: true
        });
    }
    ngOnDestroy() {
        this.observer.disconnect();
        this.cdRef.detach();
    }
    get role() {
        return Number(this._level) > 0 ? 'heading' : null;
    }
    get subtitleItems() {
        return [].concat(this.subtitleText || []);
    }
    get hasLowButtonAction() {
        return this.onlyActionsMatches('se-fe-button.se-fe-button--low');
    }
    get hasIconAction() {
        return this.onlyActionsMatches('se-fe-icon-button');
    }
    updateContent() {
        this.hasSubtitle = this.elementHasContent(this.subtitleContent);
        this.hasTitleExtra = this.elementHasContent(this.titleExtraContent);
        this.hasVisual = this.elementHasContent(this.visualContent);
        this.hasActions = this.elementHasContent(this.actionsContent);
    }
    elementHasContent(ref) {
        var _a;
        const el = ref === null || ref === void 0 ? void 0 : ref.nativeElement;
        return !!el && (!!el.childElementCount || !!((_a = el.innerText) === null || _a === void 0 ? void 0 : _a.replace(/\s/g, '')));
    }
    onlyActionsMatches(selector) {
        var _a;
        const el = (_a = this.actionsContent) === null || _a === void 0 ? void 0 : _a.nativeElement;
        if ((el === null || el === void 0 ? void 0 : el.childElementCount) !== 1)
            return;
        return el.children.item(0).matches(selector);
    }
    get element() {
        return this.el.nativeElement;
    }
}
SeFeHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SeFeHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeHeaderComponent, selector: "se-fe-header", inputs: { titleText: "titleText", seFeDataTitle: "seFeDataTitle", subtitleText: "subtitleText", seFeDataSubtitle: "seFeDataSubtitle", size: "size", seFeDataSize: "seFeDataSize", _level: "_level", seFeDataLevel: "seFeDataLevel" }, viewQueries: [{ propertyName: "actionsContent", first: true, predicate: ["actionsContent"], descendants: true, read: ElementRef, static: true }, { propertyName: "titleExtraContent", first: true, predicate: ["titleExtraContent"], descendants: true, read: ElementRef, static: true }, { propertyName: "subtitleContent", first: true, predicate: ["subtitleContent"], descendants: true, read: ElementRef, static: true }, { propertyName: "visualContent", first: true, predicate: ["visualContent"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div class=\"se-fe-header se-fe-header--size-{{size}}\" [ngClass]=\"{ 'se-fe-header--no-subtitle': !hasSubtitle }\">\n  <div #visualContent [hidden]=\"!hasVisual\" class=\"se-fe-header__visual\">\n    <ng-content select=\"[seFeHeaderVisual]\"></ng-content>\n  </div>\n  <div class=\"se-fe-header__text-and-actions\">\n    <div class=\"se-fe-header__text\">\n      <div [attr.role]=\"role\" [attr.aria-level]=\"level\" class=\"se-fe-header__title\">\n        <span *ngIf=\"titleText\">{{ titleText }}</span>\n        <ng-content *ngIf=\"!titleText\" select=\"[seFeHeaderTitle]\"></ng-content>\n      </div>\n      <div #titleExtraContent [hidden]=\"!hasTitleExtra\" class=\"se-fe-header__title-extra\">\n        <ng-content select=\"[seFeHeaderTitleExtra]\"></ng-content>\n      </div>\n      <div #subtitleContent [hidden]=\"!hasSubtitle\" class=\"se-fe-header__subtitle\">\n        <se-fe-inline-list *ngIf=\"subtitleItems.length\">\n          <se-fe-inline-list-item *ngFor=\"let subtitle of subtitleItems\">{{ subtitle }}</se-fe-inline-list-item>\n        </se-fe-inline-list>\n        <ng-content *ngIf=\"!subtitleItems.length\" select=\"[seFeHeaderSubtitle]\"></ng-content>\n      </div>\n    </div>\n    <div\n      #actionsContent\n      class=\"se-fe-header__actions\"\n      [ngClass]=\"{\n        'se-fe-header__actions--low-button': hasLowButtonAction,\n        'se-fe-header__actions--icon': hasIconAction\n      }\"\n      [hidden]=\"!hasActions\">\n      <ng-content select=\"[seFeHeaderActions]\"></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [":host{color:var(--se-text);font:var(--se-body-font);display:block}.se-fe-header{align-items:center;display:flex;flex-wrap:nowrap;justify-content:space-between}.se-fe-header__visual{align-self:flex-start;display:flex;flex:0 0 auto;padding-right:12px}.se-fe-header--no-subtitle .se-fe-header__visual{align-self:center}.se-fe-header__text-and-actions{display:flex;flex:100 1 auto;flex-wrap:nowrap;margin:-2px -4px}.se-fe-header--no-subtitle .se-fe-header__text-and-actions{align-items:center}.se-fe-header__text{align-items:center;display:flex;flex:1000 10000 auto;flex-wrap:wrap}.se-fe-header__title{display:inline-block;padding:2px 4px;vertical-align:middle}.se-fe-header--size-default .se-fe-header__title{font:var(--se-title-font)}.se-fe-header--size-largeDisplay .se-fe-header__title{font:var(--se-large-display-title-font)}.se-fe-header--size-display .se-fe-header__title{font:var(--se-display-title-font)}.se-fe-header--size-page .se-fe-header__title{font:var(--se-page-title-font)}.se-fe-header--size-section .se-fe-header__title{font:var(--se-section-title-font)}.se-fe-header--size-small .se-fe-header__title{font:var(--se-small-title-font)}.se-fe-header--size-500 .se-fe-header__title{font:var(--se-small-title-font)}.se-fe-header--size-600 .se-fe-header__title{font:var(--se-title-font)}.se-fe-header--size-700 .se-fe-header__title{font:var(--se-section-title-font)}.se-fe-header--size-750 .se-fe-header__title{font:var(--se-page-title-font)}.se-fe-header__title-extra{display:inline-block;padding:2px 4px;vertical-align:middle}.se-fe-header__subtitle{flex:0 0 100%;color:var(--se-text);margin-bottom:-.25em;padding:2px 4px}.se-fe-header__subtitle>se-fe-inline-list{margin-right:0}.se-fe-header__actions{flex:0 1 auto;padding-left:24px}.se-fe-header [hidden]{display:none}\n"], components: [{ type: i1.SeFeInlineListComponent, selector: "se-fe-inline-list" }, { type: i1.SeFeInlineListItemComponent, selector: "se-fe-inline-list-item" }], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-header',
                    templateUrl: './se-fe-header.component.html',
                    styleUrls: ['./se-fe-header.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { titleText: [{
                type: Input
            }], seFeDataTitle: [{
                type: Input,
                args: ['seFeDataTitle']
            }], subtitleText: [{
                type: Input
            }], seFeDataSubtitle: [{
                type: Input,
                args: ['seFeDataSubtitle']
            }], size: [{
                type: Input
            }], seFeDataSize: [{
                type: Input,
                args: ['seFeDataSize']
            }], _level: [{
                type: Input
            }], seFeDataLevel: [{
                type: Input,
                args: ['seFeDataLevel']
            }], actionsContent: [{
                type: ViewChild,
                args: ['actionsContent', { read: ElementRef, static: true }]
            }], titleExtraContent: [{
                type: ViewChild,
                args: ['titleExtraContent', { read: ElementRef, static: true }]
            }], subtitleContent: [{
                type: ViewChild,
                args: ['subtitleContent', { read: ElementRef, static: true }]
            }], visualContent: [{
                type: ViewChild,
                args: ['visualContent', { read: ElementRef, static: true }]
            }] } });

class SeFeHeaderModule {
}
SeFeHeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeHeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderModule, declarations: [SeFeHeaderComponent], imports: [CommonModule, SeFeInlineListModule], exports: [SeFeHeaderComponent] });
SeFeHeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderModule, imports: [[CommonModule, SeFeInlineListModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SeFeHeaderComponent],
                    imports: [CommonModule, SeFeInlineListModule],
                    exports: [SeFeHeaderComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeHeaderComponent, SeFeHeaderModule };
